import {
    IActivityRunProgress,
    IApproveActivityRun,
    ICopyJobActivityRunProgress,
    IMonitorFilter
} from 'app/model/monitoring/monitoring-models';
import { IAzureResource } from './IAzureResource';
import { IPipelineRunResponse } from './IPipelineRunResponse';
import { IResponseOptionsArgs } from 'app/services/http/interfaces';
import {
    IArtifact,
    IDatatype,
    type ITridentResource,
    LoadingStatus as NamedLoadingStatus,
    Named,
    Pipeline,
    TemplateActivityFilter
} from 'shell-models-lib';
import { Model } from 'app/model/backendmodel';
import { IResourceContext } from 'app/multi-contexts/resource-context.typings';
import { IFeatureValue } from 'app/services/exposure-control/ec.typings';
import { MatDialogConfig } from '@angular/material/dialog';
import {
    ArtifactSchedulePayload,
    ArtifactTypeV2,
    CreateArtifactPayload,
    CreateArtifactScheduleParams,
    TridentArtifactTypeV2,
    UpdateArtifactScheduleParams
} from '@trident/data-access-artifact';
import type { Permissions as FabricPermissions } from '@trident/extension-client';
import type { AccountInfo } from '@azure/msal-common';
import { ICopyJobRunResponse } from 'app/interfaces/ICopyJobRunResponse';
import { IScheduledJobsResponse } from 'app/interfaces/IScheduledJobsResponse';
import type {
    INavigationPayload,
    IPipelineCopilotContext,
    IUpdateActivityDescriptionsPayload,
    IUpdatePipelinePayload,
} from './copilot.pipeline.action.types';
import { IDiscoveredVariables } from './IDiscoveredVariables';
import { IOpenUnifiedGetData } from 'app/iframe-client/iframe-client.typings';
import {
    TridentApiServiceMethodName,
    TridentApiServiceMethodParams,
    TridentApiServiceName,
    TridentGetPipelineCopilotResponseParams
} from './TridentApiServiceDefinitions';
import type { EventOptions } from '@trident/certified-events';
import { IDbtProfileConfig } from 'app/authoring/dbt-job/wizard/dbtWizard.types';
import type { IThemeType } from 'app/theme/interfaces';

export type IFrameMessageType = CommonIFrameMessageType | TridentIFrameMessageType;

export enum TridentMonitorBasePath {
    Authoring = 'artifactAuthor',
    Artifacts = 'artifacts',
    Monitoring = 'monitoring'
}

// Important: order matters, only add types at the end of this enum
export enum CommonIFrameMessageType {
    Initialize,
    GetResources,
    OpenResource,
    ResourceCode,
    RequestResourceCode,
    Refresh,
    DebugSession,
    InsertScript,
    ExportScript,
    RequestExportScript,
    ExportExpression,
    RequestExportExpression,
    RequestImportExpression,
    OperationSucceeded,
    LoadMonitoring
}

// #region For Trident iframe message
export enum TridentIFrameMessageType {
    AddActivity = 'AddActivity',
    AutoAlign = 'AutoAlign',
    SaveResource = 'SaveResource',
    CloseDialog = 'CloseDialog',
    ClosePanel = 'ClosePanel',
    CloseTab = 'CloseTab',
    CreatePipeline = 'CreatePipeline',
    DeleteResource = 'DeleteResource',
    DetectChildPipeline = 'DetectChildPipeline',
    ExecuteCopilotAppAction = 'ExecuteCopilotAppAction',
    ExportTemplate = 'ExportTemplate',
    GetPipelineCopilotContext = 'GetPipelineCopilotContext',
    ImportTemplate = 'ImportTemplate',
    OpenTemplateGallery = 'OpenTemplateGallery',
    ListResources = 'ListResources',
    ListMetadata = 'ListMetadata',
    GetStyleFileUrl = 'GetStyleFileUrl',
    GrantPermission = 'GrantPermission',
    DialogClosed = 'DialogClosed',
    PanelClosed = 'PanelClosed',
    DismissNotification = 'DismissNotification',
    ExecExploratoryAPI = 'ExecExploratoryAPI',
    GetActivityRunProgress = 'GetActivityRunProgress',
    GetCopyJobActivityRunProgress = 'GetCopyJobActivityRunProgress',
    PushJobState = 'PushJobState',
    GetConnectionAuthoringLink = 'GetConnectionAuthoringLink',
    GetMonitorBasePath = 'GetMonitorBasePath',
    FetchJobHistory = 'FetchJobHistory',
    FetchJobInstance = 'FetchJobInstance',
    FetchSchedules = 'FetchSchedules',
    RequestFetchSchedule = 'RequestFetchSchedule',
    GetData = 'GetData',
    GetExtensionHostOriginLink = 'GetExtensionHostOriginLink',
    GetToken = 'GetToken',
    GetAllAccounts = 'GetAllAccounts',
    GetUserProfile = 'GetUserProfile',
    HostRequestPipelineRun = 'HostRequestPipelineRun',
    HostRequestCopyJobRun = 'HostRequestCopyJobRun',
    HostRequestSaveScheduledJobs = 'HostRequestSaveScheduledJobs',
    HostRequestOpenGetData = 'HostRequestOpenGetData',
    LogEvent = 'LogEvent',
    OpenActivityRuns = 'OpenActivityRuns',
    OpenCopyJobRun = 'OpenCopyJobRun',
    MonitorBasePathUpdate = 'MonitorBasePathUpdate',
    OpenCodeEditor = 'OpenCodeEditor',
    OpenDialog = 'OpenDialog',
    OpenNotification = 'OpenNotification',
    LoginPopup = 'LoginPopup',
    Navigate = 'Navigate',
    OpenPage = 'OpenPage',
    OpenPanel = 'OpenPanel',
    QueryActivityRuns = 'QueryActivityRuns',
    ApproveActivityRun = 'ApproveActivityRun',
    QueryDbtItemRuns = 'QueryDbtItemRuns',
    QueryCopyJobRun = 'QueryCopyJobRun',
    QueryCopyJobRuns = 'QueryCopyJobRuns',
    QueryPipelineRun = 'QueryPipelineRun',
    QueryPipelineRuns = 'QueryPipelineRuns',
    CancelPipelineRun = 'CancelPipelineRun',
    CancelCopyJobRun = 'CancelCopyJobRun',
    ResetCopyJob = 'ResetCopyJob',
    RequestResetCopyJob = 'RequestResetCopyJob',
    SaveCurrentArtifact = 'SaveCurrentArtifact',
    RequestCancelRun = 'RequestCancelRun',
    RerunPipeline = 'RerunPipeline',
    GetLibraryVariables = 'GetLibraryVariables',
    OpenLibraryVariablePicker = 'OpenLibraryVariablePicker',
    GetTriggers = 'GetTriggers',
    NewTrigger = 'NewTrigger',
    EditTrigger = 'EditTrigger',
    // *** #region Logic Apps activity messages ***
    RequestLaGetActions = 'RequestLaGetActions',
    RequestLaGetConnections = 'RequestLaGetConnections',
    RequestLaGetConnectors = 'RequestLaGetConnectors',
    RequestLaGetConnectorSwagger = 'RequestLaGetConnectorSwagger',
    RequestLaGetTriggers = 'RequestLaGetTriggers',
    RequestLaCreateConnection = 'RequestLaCreateConnection',
    RequestLaGetConnection = 'RequestLaGetConnection',
    RequestLaDynamicInvoke = 'RequestLaDynamicInvoke',
    DetectLogicAppsActivity = 'DetectLogicAppsActivity',
    LogicAppsActivityAction = 'LogicAppsActivityAction',
    // *** #endregion Logic Apps activity messages ***
    RequestPipelineRun = 'RequestPipelineRun',
    RequestTriggerRun = 'RequestTriggerRun',
    RequestValidate = 'RequestValidate',
    RequestPublishAll = 'RequestPublishAll',
    ResynchronizeModelServiceResource = 'ResynchronizeModelServiceResource',
    ResynchronizeModelService = 'ResynchronizeModelService',
    CallApiService = 'CallApiService',
    SetShowAuthoringTab = 'SetShowAuthoringTab',
    Zoom = 'Zoom',
    ZoomToFit = 'ZoomToFit',
    ClickEvent = 'ClickEvent',
    KeyDownEvent = 'KeyDownEvent',
    GetECValues = 'GetECValues',
    GetECFeaturesList = 'GetECFeaturesList',
    GetIframeCustomProfile = 'GetIframeCustomProfile',
    DiscardAll = 'DiscardAll',
    UpdateTab = 'UpdateTab',
    UpdateCopilotSession = 'UpdateCopilotSession',
    CreateArtifact = 'CreateArtifact',
    OpenArtifact = 'OpenArtifact',
    GetReservedNames = 'GetReservedNames',
    ConfirmDialogClose = 'ConfirmDialogClose',
    UpdateStoredArtifacts = 'UpdateStoredArtifacts',
    ViewNotificationActionDetails = 'ViewNotificationActionDetails',
    EnsureResourceContextModule = 'EnsureResourceContextModule',
    OpenMountedDataFactory = 'OpenMountedDataFactory',
    RequestSaveResource = 'RequestSaveResource',
    CosmosDbWorkloadRequest = 'CosmosDbWorkloadRequest',
    GetWorkspaceEndpoints = 'GetWorkspaceEndpoints',
    GetOneLakeApiEndpoint = 'GetOneLakeApiEndpoint',
    RequestPipelineJsons = 'RequestPipelineJsons',
    CheckCopilotAvailability = 'CheckCopilotAvailability',
    CheckFeedbackAvailability = 'CheckFeedbackAvailability',
    GetCopilotResponse = 'GetCopilotResponse',
    OpenFeedbackDialog = 'OpenFeedbackDialog',
    RequestGetRibbonButtonLocation = 'RequestGetRibbonButtonLocation',
    RequestSaveAll = 'RequestSaveAll',
    GetDataflowParameters = 'GetDataflowParameters',
    GetNotebookContent = 'GetNotebookContent',
    GetLakehouseTables = 'GetLakehouseTables',
    GetLakehouseSchemas = 'GetLakehouseSchemas',
    GetLakehouseSchemasEnabled = 'GetLakehouseSchemasEnabled',
    OpenSubfolderPickerDialog = 'OpenSubfolderPickerDialog',
    GetWorkspaceNetworkSecuritySettings = 'GetWorkspaceNetworkSecuritySettings',
    ModelServiceDoneFetching = 'ModelServiceDoneFetching',
    GetConnectionViaGetDataExperience = 'GetConnectionViaGetDataExperience',
    EditConnectionViaGetDataExperience = 'EditConnectionViaGetDataExperience',
    CreateStorageTrigger = 'CreateStorageTrigger',
    CreateDataPipeline = 'CreateDataPipeline',
    UpdateDataPipeline = 'UpdateDataPipeline',
    CreateDataflowFabric = 'CreateDataflowFabric',
    UpdateDataflowFabric = 'UpdateDataflowFabric',
    CreateFolder = 'CreateFolder',
    ListFolders = 'ListFolders',
    UpdateTheme = 'UpdateTheme',
    GetEventStreamArtifactModel = 'GetEventStreamArtifactModel',
    PreviewEventStream = 'PreviewEventStream',
    MdfDirtyStateChanged = 'MdfDirtyStateChanged',
    CreateCopyJobOneLakeEventTrigger = 'CreateCopyJobOneLakeEventTrigger',
    OpenCreateTumblingWindowTrigger = 'OpenCreateTumblingWindowTrigger',
    OpenManageTumblingWindowTriggers = 'OpenManageTumblingWindowTriggers',
    CreateTumblingWindowTrigger = 'CreateTumblingWindowTrigger',
    GetTumblingWindowTrigger = 'GetTumblingWindowTrigger',
    UpdateTumblingWindowTrigger = 'UpdateTumblingWindowTrigger',
    DeleteTumblingWindowTrigger = 'DeleteTumblingWindowTrigger',
    StartTumblingWindowTrigger = 'StartTumblingWindowTrigger',
    StopTumblingWindowTrigger = 'StopTumblingWindowTrigger',
    QueryTumblingWindowTriggers = 'QueryTumblingWindowTriggers'
}

// #endregion

export enum IFrameDebugAction {
    StartSession,
    StopSession,
    ExecuteSession,
    ExecuteCommand,
    PollSession,
    QuerySessions
}

export enum IFrameLogicAppsActivityAction {
    AddActivity,
    DeleteActivity
}

export enum TridentCdnFallbackTimeKey {
    AdfWebsiteCdnFallbackTime = 'adfWebsiteCdnFallbackTime'
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IIFrameMessageData {}

export interface IIFrameMessage<T = IIFrameMessageData> {
    sessionId?: string;
    id?: string;
    correlationId?: string;
    // eslint-disable-next-line
    type: IFrameMessageType;
    data?: T;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any;
}

export interface IIFrameResourceContextBase {
    contextData: IResourceContext;
}

export interface IIFrameResourceObjectBase {
    resourceObjectID: string;
}

export interface IIFrameExecuteCopilotAppActionData extends IIFrameResourceActionBaseData {
    action: string;
    payload?: IUpdatePipelinePayload | IUpdateActivityDescriptionsPayload | INavigationPayload;
}

export interface IIFrameExecuteCopilotAppActionResponseData extends IIFrameResourceActionBaseData {
    isRenderSuccess: boolean;
    validationResult?: IValidationResult;
}

export type IValidationResult = Array<{
    messageType: string;
    attributeDisplayName: string;
    ownedName: string;
    ownedType: string;
    supportAutoFillCopilotInput: boolean;
    attributeDataType?: IDatatype;
    attributeModelType?: string;
    activityName?: string;
    activityType?: string;
    connectionLocation?: 'Source' | 'Destination';
}>;

export interface IIFrameGetECValueRequestData extends IIFrameTridentWorkspaceBaseData, IIFrameResourceContextBase {
    features: IFeatureValue[];
}

export interface IIFrameGetECValueResponseData extends IIFrameTridentWorkspaceBaseData, IIFrameResourceContextBase {
    exposureControlResponses: IFeatureValue[];
}

export type IIFrameGetECFeaturesListRequestData = IIFrameTridentWorkspaceBaseData;

export interface IIFrameGetECFeaturesListResponseData extends IIFrameTridentWorkspaceBaseData {
    featureList: IFeatureValue[];
}

export interface IIFrameResynchronizeModelServiceResourceRequestData extends IIFrameResourceContextBase {
    resourceType: string;
}

export interface IIFrameDebugRequestData extends IIFrameMessageData {
    debugAction: IFrameDebugAction;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    debugRequest: any;
}

export interface IIFrameDebugActionData extends IIFrameMessageData {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    debugSessionRespone?: any; // fixing typo since this field is consumed externally but leaving for backwards compatibility
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    debugSessionResponse: any;
}

export interface IIFrameLogicAppsAuthenticationResponseData extends IIFrameMessageData {
    authenticatedSuccessfully: boolean;
}

export interface IIFrameTridentWorkspaceBaseData {
    /**
     * The object id of the workspace to which the artifact should be created.
     */
    workspaceObjectId: string;
    /**
     * The capacity object id of the workspace
     */
    capacityObjectId?: string;
}

export interface IIFrameGetLibraryVariablesRequestData extends IIFrameTridentWorkspaceBaseData {
    /**
     * Optional parameter to request additional resolved details for library variables.
     * Set to "All" to retrieve metadata including itemType/connectionType, display names, etc.
     * If not provided, returns basic variable information only.
     */
    addValueDetails?: 'All';
}

export type IIFrameCreatePipelineRequestData = IIFrameTridentWorkspaceBaseData;

export interface IReflexTriggerData {
    reflexId: string;
    displayName: string;
    workspaceObjId: string;
    type: string;
    state?: string;
    status?: string;
    refreshed?: string;
    nextRefresh?: string;
}

export interface IIFrameGetTriggersRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    artifactId: string;
}

export interface IIFrameEditTriggerRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    artifactId: string;
    reflexId: string;
}

export interface IIFrameGetTriggersResponseData extends IIFrameMessageData {
    results: IReflexTriggerData[];
}

export interface IIFrameCloseSideNavRequestData extends IIFrameResourceContextBase {
    fromOutside?: boolean;
    fromInside?: boolean;
}

export interface IIFrameOpenMonitoringPageRequestData extends IIFrameResourceContextBase {
    workspaceName?: string;
    pipelineRunId: string;
    pipelineName: string;
}

export interface IIFrameOpenCopyJobRunPageRequestData extends IIFrameResourceActionBaseData {
    runId: string;
}

export interface IIFrameNavigateRequestData extends IIFrameMessageData {
    path: string;
    queryParams?: Record<string, string>;
}

export interface IIFrameOpenPageRequestData extends IIFrameMessageData {
    path: string;
    queryParams?: Record<string, string>;
}

export interface IIFrameOpenDialogRequestData extends MatDialogConfig {
    header?: string;
    subHeader?: string;
    showBottomBorder?: boolean;
    needConfirmBeforeClose?: boolean;
    useMadridDimensions?: boolean;
    madridWidth?: string;
    madridHeight?: string;
}

export interface IIFrameGetActivityRunProgressRequestData extends IIFrameTridentWorkspaceBaseData {
    pipelineName: string; // pipeline oid
    activityRunId: string;
    pipelineRunId: string;
}

export interface IIFrameQueryCopyJobActivityRunProgressRequestData extends IIFrameTridentWorkspaceBaseData {
    runId: string;
    activityRunId: string;
    artifactObjectId: string;
}

export interface IIFramePushJobStateRequestData extends IIFrameQueryPipelineRunRequestData {
    artifactObjectId: string;
}

export interface IIFrameGetActivityRunProgressResponseData extends IIFrameMessageData {
    result: IActivityRunProgress;
}

export interface IIFrameGetCopyJobActivityRunProgressResponseData extends IIFrameMessageData {
    result: ICopyJobActivityRunProgress;
}

export interface IIFrameGetLibraryVariablesResponseData extends IIFrameMessageData {
    result: IDiscoveredVariables;
}

export interface IIFrameOpenLibraryVariablePickerResponseData extends IIFrameMessageData {
    libraryVariableTemplateString: string; // Example: "$(/**/Library1/variable1)"
}

export interface IIFrameArtifactRequestBaseData extends IIFrameTridentWorkspaceBaseData {
    artifactObjectIds: string[];
}

export interface IIFrameCreatePipelineRunRequestData extends IIFrameArtifactRequestBaseData, IIFrameMessageData {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pipeline?: any;
    sandboxId?: string;

    // for trigger run
    parameters?: {};
}

export type IIFrameCreateCopyJobRunRequestData = IIFrameArtifactRequestBaseData & IIFrameMessageData;

export type IIFrameSaveScheduledJobsRequestData =
    | IIFrameCreateScheduledJobsRequestData
    | IIFrameUpdateScheduledJobsRequestData;

export type IIFrameOpenGetDataRequestData = IIFrameResourceContextBase & IIFrameResourceObjectBase;

export interface IIFrameCreateScheduledJobsRequestData extends IIFrameArtifactRequestBaseData, IIFrameMessageData {
    artifactObjectIds: [string];
    operation: 'create';
    payload: CreateArtifactScheduleParams['payload'];
}

export interface IIFrameUpdateScheduledJobsRequestData extends IIFrameArtifactRequestBaseData, IIFrameMessageData {
    artifactObjectIds: [string];
    operation: 'update';
    payload: UpdateArtifactScheduleParams['payload'];
}

export interface IIFrameCreatePipelineRunResponseData extends IIFrameMessageData {
    result: IPipelineRunResponse;
}

export interface IIFrameCreateCopyJobRunResponseData extends IIFrameMessageData {
    result: ICopyJobRunResponse;
}

export interface IIFrameSaveScheduledJobsResponseData extends IIFrameMessageData {
    result: IScheduledJobsResponse;
}

export interface IIFrameQueryPipelineRunRequestData extends IIFrameTridentWorkspaceBaseData {
    runId: string;
}

export type IIFrameQueryCopyJobRunRequestData = IIFrameQueryPipelineRunRequestData;

export interface IIFrameQueryActivityRunsRequestData extends IIFrameQueryPipelineRunRequestData {
    payload: IMonitorFilter;
}

export interface IIFrameApproveActivityRunRequestData {
    payload: IApproveActivityRun;
    runId: string;
    workspaceObjectId: string;
}

export interface IIFrameApproveActivityRunResponseData {
    result: Model.ApproveActivityRunResponse;
}

export interface IIFrameQueryDbtItemRunsRequestData extends IIFrameQueryPipelineRunRequestData {
    payload: IMonitorFilter;
    artifactId: string;
}

export interface IIFrameQueryCopyJobRunsRequestData extends IIFrameQueryActivityRunsRequestData {
    artifactObjectId: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IIFrameQueryPipelineRunsRequestData extends IIFrameQueryActivityRunsRequestData {}

export interface IIFrameQueryPipelineRunResponseData extends IIFrameMessageData {
    result: Model.PipelineRun;
}

export interface IIFrameQueryCopyJobRunResponseData extends IIFrameMessageData {
    result: Model.CopyJobRun;
}

export interface IIFrameQueryActivityRunsResponseData extends IIFrameMessageData {
    result: Model.ActivityRunsListResponse;
}

export interface IIFrameQueryPipelineRunsResponseData extends IIFrameMessageData {
    result: Model.PipelineRunsQueryResponse;
}

export type IIFrameCancelPipelineRunRequestData = IIFrameUpdateRunData;

export type IIFrameCancelCopyJobRunRequestData = IIFrameUpdateRunData;

export interface IIFrameResetCopyJobRequestData extends IIFrameResourceActionBaseData {
    resetAllActivities: boolean;
    activitiesToReset?: Array<{ activityId: string }>;
}

export interface IIFrameCancelPipelineRunResponseData extends IIFrameMessageData {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result: any;
}

export interface IIFrameCancelCopyJobRunResponseData extends IIFrameMessageData {
    result: unknown;
}

export interface IIFrameResetCopyJobResponseData extends IIFrameMessageData {
    result: unknown;
}

export type IIFrameBrowseVariableLibraryResponseData =
    | {
          raw: string;
          status: 'selected';
          libraryName?: string;
          variableName?: string;
      }
    | {
          status: 'cancelled';
      };

export interface IIFrameRerunPipelineRequestData extends IIFrameTridentWorkspaceBaseData {
    artifactObjectId: string;
    runId: string;
    startActivityName?: string;
    startFromFailure?: boolean;
}

export interface IIFrameRerunPipelineResponseData extends IIFrameMessageData {
    result: Model.CreateRunResponse;
}

// from @trident schedule model
export declare enum IArtifactJobStatus {
    NotStarted = 0,
    InProgress = 1,
    Completed = 2,
    Failed = 3,
    Cancelled = 4,
    NotFound = 5,
    Duplicate = 6,
    OwnerUserMissing = 7
}

// from @trident schedule model
export interface IOwnerUser {
    id: string;
    name: string;
    objectId: string;
    userPrincipalName: string;
}

// from @trident schedule model
export interface IArtifactJobInstance {
    artifactJobInstanceId: string;
    artifactId: number;
    artifactObjectId: string;
    artifactJobType: string;
    status: IArtifactJobStatus;
    statusString: string;
    isSuccessful: boolean;
    jobScheduleTimeUtc: string;
    jobStartTimeUtc: string;
    jobEndTimeUtc: string;
    ownerUser: IOwnerUser;
    artifactName?: string;
    workspaceName?: string;
    ArtifactJobInvokeTypeString?: string;
    serviceExceptionJson?: string;
}

export interface IIFrameScheduleMessageData extends IIFrameMessageData {
    artifactObjectId: string;
}

export interface IIFrameFetchJobHistoryRequestData extends IIFrameMessageData {
    artifactObjectId: string;
}

export interface IIFrameFetchJobInstanceRequestData extends IIFrameMessageData {
    artifactObjectId: string;
    jobInstanceId: string;
}

export interface IIFrameFetchJobHistoryResponseData extends IIFrameMessageData {
    artifactObjectId: string;
    history: IArtifactJobInstance[];
}

export interface IIFrameResourceCodeData extends IIFrameSaveResourceRequestData {
    save?: boolean;
}

export interface IIFrameSaveResourceRequestData extends IIFrameResourceActionBaseData {
    resourceName: string;
    resourceType: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resource: any;
    operation?: 'update' | 'create';
    subfolderId?: number;
}

export interface IIFrameGetResourcesRequestData extends IIFrameResourceContextBase, IIFrameTridentWorkspaceBaseData {
    resourceType: string;
    // dedicated iframe only
    resourceName?: string;
    // trident iframe only
    resourceObjectID?: string;
    shallowGet?: boolean;
    resourceSubType?: string;
    // When true, the receiver should bypass any cached response and re-fetch. Set by the
    // metadata client when the user clicks Refresh on a picker. Mirrors the existing `force`
    // flag on `trident-iframemetadata-client.listResources()` options.
    forceRefresh?: boolean;
}

export interface IIFrameGetResourcesResponseData extends IIFrameMessageData {
    resources: IAzureResource[] | ITridentResource[];
}

export interface IIFrameGrantPermissionRequestData {
    workspaceId: string;
    aadAppId: string;
    artifactId?: string;
    permission?: FabricPermissions;
    artifactType?: ArtifactTypeV2;
    isServicePrincipal?: boolean;
}

/**
 * 'fabric.connections' is the general purpose get data, or UnifiedGetData or MGD or MEditor or ConnectToDatasource.
 */
export interface IIFrameGetDataRequestData extends IIFrameMessageData {
    flow: 'pipeline.copy' | 'mount' | 'copyjob' | 'refresh' | 'edit.fabric.connections' |'fabric.connections' | 'dbt.wizard';
}

export interface IIFrameOpenGetDataWizardData extends IOpenUnifiedGetData, IIFrameGetDataRequestData {}

export interface IIFrameOpenCopyAssistantData extends IIFrameResourceActionBaseData, IIFrameGetDataRequestData {
    destinationType?: string;
    destinationArtifactId?: string;
    title?: string;
}

export interface IIFrameOpenCopyJobWizardData extends IIFrameGetDataRequestData, IIFrameResourceActionBaseData {
    configureMode: 'create' | 'edit'; // default to create
    fromStage?: 'chooseData' | 'editDataMapping' | 'advancedSettings' | 'getData';
    sourceArtifactId?: string;
    sourceType?: string;
    destinationType?: string;
    destinationArtifactId?: string;
    /**
     * When true, the wizard runs as an embedded create-and-configure overlay: it persists the
     * pre-created copy job headlessly (via an update save, no navigation) and resolves a
     * ICopyJobWizardOutput back to the caller. Used by the partner action `create.copy-job.wizard`.
     */
    embedded?: boolean;
}

/**
 * Result returned by the embedded CopyJob wizard (create.copy-job.wizard) back to the caller.
 */
export interface ICopyJobWizardOutput {
    copyJobId: string;
    succeeded: boolean;
    sourceType?: string;
    sourceId?: string;
    destinationType?: string;
    destinationId?: string;
}

export interface IIFrameOpenDbtJobWizardData extends IIFrameGetDataRequestData, IIFrameResourceActionBaseData {
    wizardMode: 'sampleData' | 'gitHub' | 'importData' | 'importSchema' | 'chooseDestination' | 'editDestination' | 'selectConnection';
    existingProfileConfig?: IDbtProfileConfig;
    hideSeed?: boolean;
    fusionConfigured?: boolean;
}

export interface IIFrameOpenMountWizardData extends IIFrameResourceContextBase, IIFrameGetDataRequestData {
    mountArtifactId: string;
    configureMode?: 'create' | 'edit';
    sourceType?: string;
    artifactPayload?: string;
}

export enum TridentActivityType {
    Notebook = 'notebook',
    Copy = 'copy',
    CopyJob = 'copyJob'
}

export interface INotebookActivityTypeProperties {
    notebookId: string;
    workspaceId: string;
}

export interface IInvokeCopyJobActivityTypeProperties {
  copyJobId: string;
  workspaceId: string;
}

export interface IPipelineActivity {
    pipelineActivityType: TridentActivityType;
    pipelineActivityTypeProperties: INotebookActivityTypeProperties | IInvokeCopyJobActivityTypeProperties;
}

export interface IIFrameResourceActionBaseData extends IIFrameResourceContextBase, IIFrameTridentWorkspaceBaseData {
    resourceObjectID: string;
}

export interface IIFrameDiscardChangeData extends IIFrameResourceActionBaseData {
    showStartPageIfEmpty?: boolean;
}

export interface IIFrameUpdateRunData extends IIFrameResourceActionBaseData {
    runId: string;
}

export interface IIFrameOpenResourceInTridentWsData extends IIFrameResourceActionBaseData {
    resourceType: 'pipeline' | 'copyjob';
    /**
     * Artifact workload payload, if present, we can use that to load model directly. If not present, we have
     * to send a request to fetch the artifact first.
     */
    resource?: ITridentResource;
    activity?: IPipelineActivity;
}

export interface IIFrameOpenResourceInDedicatedIFrameData {
    resourceName: string;
    resourceType: string;
}

export interface IIFrameOpenResourceInMDFIFrameData {
    resourceName: string;
    resourceType: string;
    resourceObjectID: string;
}

export type IIFrameOpenResourceData = IIFrameOpenResourceInTridentWsData | IIFrameOpenResourceInDedicatedIFrameData | IIFrameOpenResourceInMDFIFrameData;

export interface IIFrameOpenMountedDataFactory extends IIFrameResourceContextBase {
    needsResyncModelService?: boolean;
    isMigration?: boolean;
}

export interface IIFrameOpenJSONCodeRequestData extends IIFrameOpenResourceInTridentWsData {
    readonly?: boolean;
}

export interface IIFrameRequestValidateData extends IIFrameResourceActionBaseData {
    openSideNavWithNoError?: boolean;
    omitSidenavWithError?: boolean;
}

export interface IIFrameSetShowAuthoringTabData extends IIFrameResourceActionBaseData {
    showStartPage: boolean;
}

export interface IIFrameSaveTridentResourceData {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any;
    publishedArtifact?: ITridentResource;
    requestId?: string;
}

export interface IIFrameSaveResourceResponseData<T> extends IIFrameMessageData {
    saved: T;
}

export interface IIFrameScriptData extends IIFrameMessageData {
    scriptFragment: string;
    insertAfterTransformation?: string;
}

export interface IIFrameLoadMonitoringData extends IIFrameMessageData {
    activityOutput: string;
}

export interface IIFrameResultCallback {
    data: IIFrameMessageData;
    correlationId: string;
}

export interface IIFrameAddActivityData extends IIFrameMessageData, IIFrameResourceActionBaseData {
    modelName: string;
}

export interface IIFrameGetPipelineCopilotContextResponseData extends IIFrameResourceActionBaseData {
    pipelineCopilotContext: IPipelineCopilotContext;
}

export interface IIFrameZoomOptionData extends IIFrameMessageData, IIFrameResourceActionBaseData {
    zoomLevel: string;
}

export interface IIFrameGetTokenResultData extends IIFrameMessageData {
    accessToken: string;
}

export interface IIFrameGetAllAccountsResultData extends IIFrameMessageData {
    accounts: AccountInfo[];
}

export interface IIFrameLoginPopupResultData extends IIFrameMessageData {
    account: AccountInfo;
}

export interface IIFrameGetUserProfileResultData extends IIFrameMessageData {
    name: string;
    givenName: string;
    surname: string;
    principalName: string;
    objectId: string;
    tenantId: string;
}

export interface IIFrameHttpResponseData extends IIFrameMessageData {
    result: IResponseOptionsArgs;
}

export interface IIFrameAsyncOperationResult extends IIFrameMessageData {
    isSucceeded: boolean;
}

export interface IIFrameDeleteResourceRequestData extends IIFrameMessageData {
    artifactType: string;
    artifactName: string;
    objectId: string;
}

export interface IIFrameListResourcesMessageData extends IIFrameResourceContextBase, IIFrameTridentWorkspaceBaseData {
    resourceType: string;
    resourceObjectID?: string;
}

export interface IIFrameLogicAppsState {
    hasEmail?: boolean;
    hasTeams?: boolean;
    hasAzureBlobStorage?: boolean;
    hasAzureDevOps?: boolean;
    hasInvokeADFPipeline?: boolean;
}

export interface IIFrameContainsChildPipeline {
    containsChildPipeline?: boolean;
}

export interface IIFrameUpdateTridentTabData extends IIFrameResourceActionBaseData {
    objectId?: string;
    displayName?: string;
    showStartPage?: boolean;
    logicAppsState?: IIFrameLogicAppsState;
    loadError?: {
        status: NamedLoadingStatus;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error?: any;
    };
    connectionViolations?: Array<{ message: string }>;
    nestedPipelineEntityModel?: string;

    // #region status
    isSavingAs?: boolean;
    isRunning?: boolean;
    runId?: string;
    ifHasChanges?: boolean;
    isSaving?: boolean;
    isValidating?: boolean;
    // #endregion

    // #region MountedDataFactory git state
    isGitMode?: boolean;
    collaborationBranch?: string;
    workingBranch?: string;
    isAutoPublishMode?: boolean;
    // #endregion
}

export interface IIFrameUpdateCopilotSessionData extends IIFrameResourceActionBaseData {
    displayName: string;
    pipelineCopilotRequestData?: IIFramePipelineCopilotRequestData;
    showCopilot?: boolean;
}

export interface IIFrameGetConnectionAuthoringLinkRequestData extends IIFrameTridentWorkspaceBaseData {
    isEmbedded?: boolean;
    redirect?: 'editConnection' | 'createConnection';
    connectionId?: string; // for editing
    connectionTypes?: string[]; // for new
}

export interface IIFrameGetExternalPortalLinkResponseData extends IIFrameMessageData {
    origin: string;
    url?: string;
}

export enum ActivityStartMenuEnum {
    AddPipelineActivity = 'AddPipelineActivity',
    GetData = 'GetData',
    StartFromPipeline = 'StartFromPipeline'
}

export enum CopyJobStartMenuEnum {
    FromADLSGen2ToLakehouse = 'FromADLSGen2ToLakehouse',
    FromOracleToLakehouse = 'FromOracleToLakehouse',
    FromAzureSQLWhenNewDataArrives = 'FromAzureSQLWhenNewDataArrives',
    FromRealTimeDataSources = 'FromRealTimeDataSources',
    FromAnySourceToAnyDestination = 'FromAnySourceToAnyDestination'
}

export enum DbtJobProfileMenuEnum {
    SkipProfile = 'SkipProfile',
    ConfigureProfile = 'ConfigureProfile'
}

export enum LoggingLevel {
    Information = '[Information]',
    Trace = '[Trace]',
    Warning = '[Warning]',
    Error = '[Error]',
    Event = '[Event]'
}

export interface ILoggerOptions {
    eventOptions?: EventOptions;
}

export interface IIFrameLogEventRequestData extends IIFrameMessageData {
    name: LoggingLevel | string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    properties: Record<string, any>;
    options?: ILoggerOptions;
}

interface IIFrameArtifactBaseRequestData extends IIFrameMessageData {
    type: string;
}

export type IIFrameCreateArtifactRequestData = IIFrameArtifactBaseRequestData &
    IIFrameTridentWorkspaceBaseData & {
        dataFactoryId?: string;
        createArtifactPayload?: Omit<CreateArtifactPayload, 'artifactType'>;
    };

export interface ICreateArtifactResponseData extends IIFrameMessageData {
    success: boolean;
    userCancelled?: boolean;
    lakehouseInfo?: {
        id: string;
        capacityId: string;
        name: string;
    } & IIFrameTridentWorkspaceBaseData;
    artifact?: IArtifact;
}

export interface IIFrameOpenArtifactRequestData
    extends IIFrameArtifactBaseRequestData,
        IIFrameTridentWorkspaceBaseData {
    objectId: string;
    version?: string;
}

export interface IIFrameOpenPanelRequestData extends IIFrameResourceContextBase {
    width?: string;
    isLightDismiss?: boolean;
    queryParams?: {
        header?: string;
        subHeader?: string;
    };
}

export interface ICopyJobDeltaTableTriggerData {
    sourceWorkspaceId?: string;
    sourceArtifactId?: string;
    sourceConnectionId?: string;
    sourcePaths: string[];
}

export interface IIFrameTriggerRunData extends IIFrameResourceActionBaseData {
    extraActions?: Array<'schedule' | 'deltaTableTrigger'>;
    schedule?: ArtifactSchedulePayload;
    deltaTableTrigger?: ICopyJobDeltaTableTriggerData;
}

export interface IIFrameActivityRunData {
    pipelineRunId: string;
    activityRunId: string;
}

export interface IArtifactChange extends IIFrameTridentWorkspaceBaseData {
    changeType: 'add' | 'remove' | 'edit';
    artifactObjectId: string;
    artifactType: TridentArtifactTypeV2;
}

export interface IIFrameUpdateStoredArtifactsData extends IIFrameMessageData {
    changes?: IArtifactChange[];
}

export interface IIFrameConnectorRequestData extends IIFrameTridentWorkspaceBaseData {
    connectorName: string;
}

export interface IIFrameDynamicInvokeRequestData extends IIFrameMessageData, IIFrameTridentWorkspaceBaseData {
    activityType: string;
    objectId: string;
    path: string;
    method: string;
    getSchema: boolean;
    headers?: Object;
    queries?: Object;
    dmtsConnectionId?: string;
    host?: string;
}

export interface IIFrameCosmosDbWorkloadRequestData extends IIFrameMessageData {
    endpointPath: string;
    artifactObjectId: string;
    parameters: unknown;
}

export interface IIframeCallApiServiceRequestData<
    S extends TridentApiServiceName = TridentApiServiceName,
    M extends TridentApiServiceMethodName<S> = TridentApiServiceMethodName<S>
> {
    apiServiceName: S;
    methodName: M;
    params?: TridentApiServiceMethodParams<S, M>;
}

export interface IIFrameCosmosDbWorkloadResponseData extends IIFrameMessageData {
    statusCode: number;
    statusText: string;
    payload: unknown;
}

export interface IIFrameGetWorkspaceEndpointsRequestData extends IIFrameMessageData {
    /**
     * @description The workspace object id
     */
    workspaceObjectId: string;
}

export interface IIFrameGetWorkspaceEndpointsResponseData extends IIFrameMessageData {
    /**
     * @description The workspace endpoint for Shared Backend services.
     * @example https://80489668bff04b109897f831d18c8537.z80.w.api.fabric.microsoft.com
     */
    apiEndpoint?: string;
    /**
     * @description The workspace endpoint for OneLake API.
     * @example https://80489668bff04b109897f831d18c8537.z80.onelake.fabric.microsoft.com
     */
    onelakeApiEndpoint?: string;
    /**
     * @description The workspace endpoint for OneLake DFS service.
     * @example https://80489668bff04b109897f831d18c8537.z80.dfs.fabric.microsoft.com
     */
    onelakeDfsEndpoint?: string;
    /**
     * @description The workspace endpoint for OneLake blob service.
     * @example https://80489668bff04b109897f831d18c8537.z80.blob.fabric.microsoft.com
     */
    onelakeBlobEndpoint?: string;
}

export interface IIFrameGetWorkspaceNetworkSecuritySettingsRequestData extends IIFrameMessageData {
  /**
   * @description The workspace object id
   */
  workspaceObjectId: string;
}

export interface IIFrameGetWorkspaceNetworkSecuritySettingsResponseData extends IIFrameMessageData {
    /**
     * @description ETag representing the current version of the workspace-level network security settings.
     */
    etag: string;
    /**
     * @description Indicates whether the outbound access of the workspace is restricted.
     */
    restrictOutboundAccess: boolean;
    /**
     * @description Indicates whether the inbound access of the workspace is restricted.
     */
    restrictInboundAccess: boolean;
    /**
     * @description Indicates whether tenant-level DEP is enabled.
     */
    tenantDEPEnabled: boolean;
    /**
     * @description Indicates whether the workspace is eligible for restricting outbound access.
     */
    isRestrictOutboundAccessEligible: boolean;
}

export interface IStorageTriggerFilter {
    values: string[];
    operatorType: string;
    key: string;
}

export interface IStorageTriggerFilterInfo {
    includedEventTypes: string[];
    filters: IStorageTriggerFilter[];
}

export interface IIFrameCreateStorageTriggerRequestData extends IIFrameMessageData {
    storageAccountResourceId: string;
    subscriptionId: string;
    workspaceObjectId: string;
    artifactId?: string;
    artifactName?: string;
    capacityObjectId?: string;
    eventStreamName?: string;
    filterInfo?: IStorageTriggerFilterInfo;
    telemetry?: unknown;
}

export interface IIFrameCreateStorageTriggerResponseData extends IIFrameMessageData {
    triggerId?: string;
    activatorId?: string;
    success: boolean;
    error?: string;
}

export interface IIFrameCreateCopyJobOneLakeEventTriggerRequestData
    extends IIFrameMessageData,
        ICopyJobDeltaTableTriggerData {
    copyJobId: string;
    copyJobName: string;
    workspaceObjectId: string;
}

export interface IIFrameGetEventStreamArtifactModelRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    artifactId: string;
}

export interface IIFrameGetEventStreamArtifactModelResponseData extends IIFrameMessageData {
    streams: Array<{ id: string; name: string; type?: string }>;
}

export interface IIFramePreviewEventStreamRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    artifactId: string;
    streamId: string;
    numberOfEvents: number;
}

export interface IIFrameGetOneLakeApiEndpointResponseData extends IIFrameMessageData {
    /*
    * TODO: Update oneLakeApiGlobalEndpoint or oneLakeApiBlobEndpoint when we can get OneLake endpoints of workspace in ExtensionClientAPI
    * Endpoint resources:
    * @See https://powerbi.visualstudio.com/Trident/_wiki/wikis/Trident.wiki/19999/OneLake-Endpoints?anchor=global-fqdns%3A
    */
    oneLakeApiGlobalEndpoint?: string;
    oneLakeBlobEndpoint?: string;
}

export interface IDataflowParameterResponse {
    name: string;
    isRequired: boolean;
    description: string;
    type: string;
    defaultValue: string;
}

export interface IIFrameGetDataflowParametersResponseData extends IIFrameMessageData {
    parameters: IDataflowParameterResponse[];
    status: number;
}

// #region Notebook content types
export interface INotebookContentResponse {
    metadata?: INotebookMetadata;
    nbformat: number;
    nbformat_minor: number;
    cells?: NotebookCell[];
}

export interface INotebookMetadata {
    microsoft: INotebookLanguageInfo;
    spark_compute: ISparkComputeInfo;
    dependencies: INotebookDependencies;
}

export type NotebookCell = INotebookCodeCell | INotebookMarkdownCell | INotebookRawCell;

export interface INotebookCodeCell {
    cell_type: 'code';
    metadata: INotebookCellMetadata;
    execution_count: ExecutionCount;
    source: MultiLineString;
    outputs: NotebookOutput[];
    id?: string;
}

export interface INotebookMarkdownCell {
    cell_type: 'markdown';
    source: MultiLineString;
    metadata: INotebookCellMetadata;
    id?: string;
}

export interface INotebookRawCell {
    cell_type: 'raw';
    source: MultiLineString;
    metadata: INotebookCellMetadata;
    id?: string;
}

export interface INotebookCellMetadata {
    tags?: string[];
    [key: string]: unknown;
}

export type ExecutionCount = number | null;

export type MultiLineString = string | string[];

export type NotebookOutput =
    | INotebookExecuteResultOutput
    | INotebookDisplayDataOutput
    | INotebookStreamOutput
    | INotebookErrorOutput;

export interface INotebookExecuteResultOutput {
    output_type: 'execute_result';
    execution_count: ExecutionCount;
    data: Record<string, unknown>;
    metadata: Record<string, unknown>;
    transient?: Record<string, unknown>;
}

export interface INotebookDisplayDataOutput {
    output_type: 'display_data';
    data: Record<string, unknown>;
    metadata: Record<string, unknown>;
    transient?: Record<string, unknown>;
}

export interface INotebookStreamOutput {
    output_type: 'stream';
    name: 'stdout' | 'stderr';
    text: MultiLineString;
}

export interface INotebookErrorOutput {
    output_type: 'error';
    ename: string;
    evalue: string;
    traceback: string[];
}

export interface INotebookLanguageInfo {
    language: string;
    language_group: string;
}

export interface ISparkComputeInfo {
    compute_id: string;
}

export interface INotebookDependencies {
  environment?: INotebookEnvironment;
}

export interface INotebookEnvironment {
  environmentId: string;
  workspaceId: string;
}
// #endregion Notebook content types

export interface IIFrameGetNotebookContentResponseData extends IIFrameMessageData {
    content: INotebookContentResponse;
    status: number;
}

// NOTE: anredd - below two types are not complete, see extension for full details
interface ILakehouseTable {
  type: string;
  name: string;
  location: string;
  format: string;
}

interface ILakehouseSchemas {
  name: string;
  catalog_name: string;
  full_name: string;
  created_at: number | null;
  updated_at: number | null;
  comment: string | null;
}

export interface IIFrameGetLakehouseTablesResponseData extends IIFrameMessageData {
    tables: ILakehouseTable[];
}

export interface IIFrameGetLakehouseSchemasResponseData extends IIFrameMessageData {
    schemas: ILakehouseSchemas[];
}

export interface IIFrameGetLakehouseSchemasEnabledResponseData extends IIFrameMessageData {
    schemasEnabled: boolean;
}

export interface IIFramePipelineCopilotRequestData extends IIFrameTridentWorkspaceBaseData {
    skill: 'ExplainError' | 'ExplainPipeline' | 'ExplainRun';
    pipelineObjectId: string;
    /**
     * Input sent to Copilot.
     */
    input: string;

    /**
     * Display text in message box.
     */
    displayText?: string;

    /**
     * Pipeline entity context.
     */
    currentPipeline?: string;

    /**
     * Activity run failures of the pipeline.
     */
    errors?: string;
    
    /**
     * Run details for the current pipeline
     */
    pipelineRuns?: string;
    experience?: 'SummaryButton' | 'QuickAction';
    enabledSkills?: string[];
}

export interface IIFrameGetRibbonButtonRectRequest extends IIFrameMessageData {
    buttonId: string;
}

export interface IIFrameGetRibbonButtonLocationResponse extends IIFrameResourceActionBaseData {
    buttonRect: DOMRect;
}

export interface IIFramePipelinePreviewDialogData extends IIFrameResourceActionBaseData {
    named: Named;
    selectedPipeline?: Pipeline;
    pipelinesPromise: Promise<Pipeline[]>;
}

export interface IIFrameImportPipelineTemplateData extends IIFrameResourceActionBaseData {
    file: File;
}

export type IIFrameCopilotAvailabilityRequestData = IIFrameTridentWorkspaceBaseData & IIFrameResourceObjectBase;
export type IFrameGetCopilotResponseRequestData = TridentGetPipelineCopilotResponseParams;

export type IFrameOpenFeedbackDialogRequestData = {
    featureName: 'DataFactory-PipelineCopilot' | 'DataFactory-ErrorFeedback';
    scenarioType: 'Custom' | 'ThumbsUp' | 'ThumbsDown';
    attributes?: { [key: string]: string };
};

type ConfirmDialogState = 'Confirm' | 'Cancel';
export type IFrameOpenFeedbackDialogResponseData = {
    state: ConfirmDialogState;
};

export interface IIFrameCopilotAvailabilityResponseData extends IIFrameMessageData {
    disabledMessage: string;
}

export interface IIFrameFeedbackAvailabilityResponseData extends IIFrameMessageData {
    feedbackEnabled: boolean;
}

export interface IIFrameOpenTemplateGalleryData extends IIFrameResourceActionBaseData, IIFrameGetDataRequestData {
    filter?: TemplateActivityFilter;
    datasetId?: string;
}

// #region Data Pipeline iframe message data interfaces

export interface IDataPipelineDefinitionPart {
    path: string;
    payload: string;
    payloadType: 'InlineBase64';
}

export interface IDataPipelineDefinition {
    format?: string;
    parts: IDataPipelineDefinitionPart[];
}

export interface IIFrameCreateDataPipelineRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    displayName: string;
    description?: string;
    definition?: IDataPipelineDefinition;
    folderId?: string;
}

export interface IIFrameCreateDataPipelineResponseData extends IIFrameMessageData {
    id: string;
    displayName: string;
    description?: string;
    type: string;
    workspaceId: string;
    folderId?: string;
    requestId?: string;
}

export type IIFrameOpenCreateTumblingWindowTriggerRequestData = IIFrameResourceActionBaseData;
export type IIFrameOpenManageTumblingWindowTriggersRequestData = IIFrameResourceActionBaseData;

export interface IIFrameCreateTumblingWindowTriggerRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    displayName: string;
    description?: string;
    workloadPayload: string;
}

export interface IIFrameCreateTumblingWindowTriggerResponseData extends IIFrameMessageData {
    objectId: string;
    artifactType: 'TumblingWindowTrigger';
    displayName: string;
    description?: string;
    folderObjectId: string;
    provisionState: string;
    lastUpdatedDate: string;
    capacityObjectId: string;
    workloadPayload?: string;
    payloadContentType?: string;
    etag: string;
    requestId?: string;
}

export type ITumblingWindowTriggerArtifactData = IIFrameCreateTumblingWindowTriggerResponseData;

export interface IIFrameGetTumblingWindowTriggerRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    objectId: string;
}

export type IIFrameGetTumblingWindowTriggerResponseData = ITumblingWindowTriggerArtifactData;

export interface IIFrameUpdateTumblingWindowTriggerRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    objectId: string;
    etag: string;
    displayName: string;
    description?: string;
    workloadPayload: string;
}

export type IIFrameUpdateTumblingWindowTriggerResponseData = ITumblingWindowTriggerArtifactData;

export interface IIFrameDeleteTumblingWindowTriggerRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    objectId: string;
    softDelete?: boolean;
}

export interface IIFrameTumblingWindowTriggerRuntimeRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    objectId: string;
}

export interface IIFrameTumblingWindowTriggerOperationResponseData extends IIFrameMessageData {
    requestId?: string;
}

export interface IIFrameQueryTumblingWindowTriggersRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
}

export interface ITumblingWindowTriggerListArtifactData extends IIFrameMessageData {
    objectId: string;
    artifactType: 'TumblingWindowTrigger';
    displayName: string;
    workloadPayload?: string;
}

export interface IIFrameQueryTumblingWindowTriggersResponseData extends IIFrameMessageData {
    result: ITumblingWindowTriggerListArtifactData[];
}

export interface IIFrameUpdateDataPipelineDefinitionRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    dataPipelineId: string;
    definition: IDataPipelineDefinition;
    updateMetadata?: boolean;
}

export interface IIFrameUpdateDataPipelineDefinitionResponseData extends IIFrameMessageData {
    requestId?: string;
}

// #endregion Data Pipeline iframe message data interfaces

// #region DataflowFabric iframe message data interfaces

export interface IDataflowDefinitionPart {
    path: string;
    payload: string;
    payloadType: 'InlineBase64';
}

export interface IDataflowDefinition {
    parts: IDataflowDefinitionPart[];
}

export interface IIFrameCreateDataflowFabricRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    displayName: string;
    description?: string;
    definition?: IDataflowDefinition;
    folderId?: string;
}

export interface IIFrameCreateDataflowFabricResponseData extends IIFrameMessageData {
    id: string;
    displayName: string;
    description?: string;
    type: string;
    workspaceId: string;
    folderId?: string;
    requestId?: string;
}

export interface IIFrameUpdateDataflowFabricDefinitionRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    dataflowId: string;
    definition: IDataflowDefinition;
    updateMetadata?: boolean;
}

export interface IIFrameUpdateDataflowFabricDefinitionResponseData extends IIFrameMessageData {
    requestId?: string;
}

// #endregion DataflowFabric iframe message data interfaces

// #region Folder iframe message data interfaces

export interface IIFrameFolderItem {
    id: string;
    displayName: string;
    workspaceId: string;
    parentFolderId?: string;
}

export interface IIFrameCreateFolderRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    displayName: string;
    parentFolderId?: string;
}

export interface IIFrameCreateFolderResponseData extends IIFrameMessageData {
    id: string;
    displayName: string;
    workspaceId: string;
    parentFolderId?: string;
}

export interface IIFrameListFoldersRequestData extends IIFrameMessageData {
    workspaceObjectId: string;
    rootFolderId?: string;
    recursive?: boolean;
}

export interface IIFrameListFoldersResponseData extends IIFrameMessageData {
    value: IIFrameFolderItem[];
}

// #endregion Folder iframe message data interfaces

export interface IIFrameUpdateThemeData extends IIFrameMessageData {
    themeType: IThemeType;
}
